import { useCallback, useEffect, useMemo, useRef, useState } from "preact/hooks";

import {
  ApiError,
  createAssignment,
  createScheduleException,
  deleteScheduleException,
  fetchSchedule,
  patchAssignment,
} from "../lib/api";
import {
  addDays,
  addMonths,
  buildWeekDays,
  endOfMonth,
  isoWeekStart,
  isSameDay,
  parseISODate,
  startOfMonth,
  startOfWeek,
  todayLocal,
  toISODate,
} from "../lib/calendarDates";
import {
  addedSnackbarMessage,
  CALENDAR_GENERIC_ERROR,
  movedSnackbarMessage,
  moveTargetLabel,
  skippedSnackbarMessage,
  UNDO_ALREADY_DONE_ACTION_LABEL,
  UNDO_ALREADY_DONE_MESSAGE,
} from "../lib/calendarCopy";
import { monthDayLabel } from "../lib/recordsCopy";
import type { FlexibleWeekChip, ScheduleOccurrence } from "../types";
import type { SnackbarState } from "./useDue";

export type CalendarViewMode = "month" | "week";
export type CalendarScope = "mine" | "team";

export interface CalendarMutationResult {
  ok: boolean;
  conflict?: boolean;
}

interface CachedWindow {
  from: string;
  to: string;
  scope: CalendarScope;
}

const SNACKBAR_MS = 5000;

function isoRangeContains(from: string, to: string, targetFrom: string, targetTo: string): boolean {
  return from <= targetFrom && targetTo <= to;
}

/** §2.1: "covering the visible view's date span plus a small margin so
 * month/week navigation doesn't refetch on every arrow click (cache the
 * surrounding month)." A 3-month window for month view, a 5-week window
 * for week view — both comfortably under the server's own 400-day span
 * guard (ADR-002 A5 decision 1.5), and wide enough that a single Prev/Next
 * click never falls outside the cached range. */
function paddedGridWindow(viewMode: CalendarViewMode, anchor: Date): { from: Date; to: Date } {
  if (viewMode === "month") {
    return { from: startOfMonth(addMonths(anchor, -1)), to: endOfMonth(addMonths(anchor, 1)) };
  }
  const weekStart = startOfWeek(anchor);
  return { from: addDays(weekStart, -14), to: addDays(weekStart, 20) };
}

function visibleRange(viewMode: CalendarViewMode, anchor: Date): { from: Date; to: Date } {
  if (viewMode === "month") {
    // `buildMonthGrid`'s own leading/trailing padding is at most 6 days
    // either side — always inside `paddedGridWindow`'s ±1-month margin.
    return { from: startOfMonth(anchor), to: endOfMonth(anchor) };
  }
  const days = buildWeekDays(anchor);
  return { from: days[0] as Date, to: days[6] as Date };
}

export interface UseScheduleOptions {
  /** Same flag every other role-gated screen threads through — gates the
   * "My calendar"/"Team calendar" toggle (§2.1) exactly as it gates the
   * Team tab/Reports/the calendar's own write affordances. A member's
   * `scope` is forced to `"mine"` regardless of this hook's internal
   * `scope` state (server-enforced too — a member passing `scope=team`
   * is a 403 + audit denial row, ADR-002 A5 decision 1.4). */
  canHandOutWork: boolean;
  /** Where the undo-409 "Open it to see what happened" Snackbar action
   * routes (§2.9) — the manager/admin team board, since calendar writes
   * are admin/manager/api only (a member never reaches this hook's write
   * methods at all). */
  navigate: (path: string) => void;
}

/**
 * Sprint-009 B4 — the calendar's data/mutation hook (design-phase-b.md §2,
 * ADR-002 Amendment 5 decisions 1-3). Owns: the grid's fetch/cache window
 * (§2.1), the "This week" flexible band's own independent fetch (§2.6),
 * and the four write actions (create one-off, reschedule one-off,
 * reschedule/skip a cadence occurrence, undo) with their Snackbar/409
 * wiring (§2.7-§2.9). Deliberately does NOT integrate with
 * `offline/queue.ts` — calendar writes are admin/manager desktop-weighted
 * actions with a different reliability profile than a helper's task-
 * completion tap; a network failure here surfaces as a plain inline error
 * to retry (matching the Chore editor / Add Person save-failure pattern),
 * not a silent background queue entry. Flagged as a scope decision in the
 * PR, not a silently dropped requirement.
 */
export function useSchedule({ canHandOutWork, navigate }: UseScheduleOptions) {
  const [viewMode, setViewMode] = useState<CalendarViewMode>("month");
  const [anchor, setAnchor] = useState<Date>(() => todayLocal());
  const [scope, setScope] = useState<CalendarScope>("mine");
  const effectiveScope: CalendarScope = canHandOutWork ? scope : "mine";

  const [occurrences, setOccurrences] = useState<ScheduleOccurrence[]>([]);
  const [flexible, setFlexible] = useState<FlexibleWeekChip[]>([]);
  // TD-010 fix (design-phase-c.md §8's binding "TD-010-class rule") —
  // "have we EVER successfully loaded data" is a DIFFERENT boolean than
  // "is a refetch currently in flight." The old single `loading` flag
  // conflated the two: `loadWindow` set it `true` again on every call,
  // including the one `refreshCurrentWindow()` triggers after a
  // successful write (create/reschedule/skip/undo) — and
  // `CalendarScreen.tsx`'s own render-time ternary swapped the whole
  // month/week/agenda subtree for a `<p>Loading the calendar…</p>`
  // placeholder every single time, remounting `CalendarMonthView` and
  // silently discarding its own `expandedDays` "+N more" state. Only
  // `hasLoadedOnce === false` may legitimately show a full loading
  // placeholder in place of content (there is genuinely nothing yet to
  // preserve); `refreshing` is a separate, non-blocking busy signal a
  // caller can render ALONGSIDE the already-mounted view, never instead
  // of it.
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<SnackbarState | null>(null);
  const snackbarTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cacheRef = useRef<CachedWindow | null>(null);

  const showSnackbar = useCallback((message: string, onUndo?: () => void, actionLabel?: string) => {
    if (snackbarTimerRef.current) clearTimeout(snackbarTimerRef.current);
    setSnackbar({ message, onUndo, actionLabel });
    snackbarTimerRef.current = setTimeout(() => setSnackbar(null), SNACKBAR_MS);
  }, []);

  const dismissSnackbar = useCallback(() => {
    if (snackbarTimerRef.current) clearTimeout(snackbarTimerRef.current);
    setSnackbar(null);
  }, []);

  const loadWindow = useCallback(async (from: Date, to: Date, forScope: CalendarScope) => {
    setRefreshing(true);
    setError(null);
    const fromIso = toISODate(from);
    const toIso = toISODate(to);
    try {
      const response = await fetchSchedule(fromIso, toIso, forScope);
      cacheRef.current = { from: fromIso, to: toIso, scope: forScope };
      setOccurrences(response.occurrences);
      setHasLoadedOnce(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't load the calendar.");
    } finally {
      setRefreshing(false);
    }
  }, []);

  // Grid window fetch/cache (§2.1) — re-fetches only when the current
  // view's visible range falls outside the cached padded window, or scope
  // changes.
  useEffect(() => {
    const visible = visibleRange(viewMode, anchor);
    const visibleFromIso = toISODate(visible.from);
    const visibleToIso = toISODate(visible.to);
    const cached = cacheRef.current;
    if (
      cached &&
      cached.scope === effectiveScope &&
      isoRangeContains(cached.from, cached.to, visibleFromIso, visibleToIso)
    ) {
      return;
    }
    const padded = paddedGridWindow(viewMode, anchor);
    void loadWindow(padded.from, padded.to, effectiveScope);
  }, [viewMode, anchor, effectiveScope, loadWindow]);

  // "This week" flexible band (§2.6) — a small, separate fetch scoped to
  // the ACTUAL current ISO week (never the grid's navigated-to window):
  // "browsing a month ahead never shows future weeks' flexible targets."
  // Refetches only on scope change, never on grid navigation.
  useEffect(() => {
    const weekStart = isoWeekStart(todayLocal());
    const weekEnd = addDays(weekStart, 6);
    let cancelled = false;
    (async () => {
      try {
        const response = await fetchSchedule(
          toISODate(weekStart),
          toISODate(weekEnd),
          effectiveScope,
        );
        if (!cancelled) setFlexible(response.flexible);
      } catch {
        if (!cancelled) setFlexible([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [effectiveScope]);

  const refreshCurrentWindow = useCallback(async () => {
    const cached = cacheRef.current;
    if (!cached) return;
    await loadWindow(parseISODate(cached.from), parseISODate(cached.to), cached.scope);
  }, [loadWindow]);

  // Reverse indexes for §2.5's "moved" chip rendering: the nominal
  // occurrence carries `exception.linked_assignment_id`; the linked
  // one-off (on its NEW date) is just an ordinary assignment-kind
  // occurrence with no back-reference of its own — these two maps close
  // that gap client-side from the currently-cached occurrence set.
  const assignmentDateById = useMemo(() => {
    const map = new Map<string, string>();
    for (const occ of occurrences) {
      if (occ.kind === "assignment" && occ.id) map.set(occ.id, occ.date);
    }
    return map;
  }, [occurrences]);

  const movedFromByAssignmentId = useMemo(() => {
    const map = new Map<string, boolean>();
    for (const occ of occurrences) {
      if (occ.exception?.action === "moved" && occ.exception.linked_assignment_id) {
        map.set(occ.exception.linked_assignment_id, true);
      }
    }
    return map;
  }, [occurrences]);

  // §2.8's `moveTargetLabel` reference range — "this week" (the week
  // containing today), the one stable anchor regardless of which
  // month/week the grid is currently showing.
  const thisWeekDays = useMemo(() => buildWeekDays(todayLocal()), []);

  const undoException = useCallback(
    async (cadenceId: string, occurrenceDate: string) => {
      const idempotencyKey = crypto.randomUUID();
      try {
        await deleteScheduleException(cadenceId, occurrenceDate, idempotencyKey);
        await refreshCurrentWindow();
      } catch (err) {
        if (err instanceof ApiError && err.status === 409) {
          showSnackbar(
            UNDO_ALREADY_DONE_MESSAGE,
            () => navigate("/team"),
            UNDO_ALREADY_DONE_ACTION_LABEL,
          );
        } else {
          showSnackbar(CALENDAR_GENERIC_ERROR);
        }
      }
    },
    [refreshCurrentWindow, navigate, showSnackbar],
  );

  const skipOccurrence = useCallback(
    async (occ: ScheduleOccurrence): Promise<CalendarMutationResult> => {
      if (!occ.cadence_id) return { ok: false };
      const cadenceId = occ.cadence_id;
      const occurrenceDate = occ.date;
      const idempotencyKey = crypto.randomUUID();
      try {
        await createScheduleException({
          idempotency_key: idempotencyKey,
          cadence_id: cadenceId,
          occurrence_date: occurrenceDate,
          action: "suppressed",
        });
        await refreshCurrentWindow();
        showSnackbar(
          skippedSnackbarMessage(occ.title, monthDayLabel(occurrenceDate)),
          () => void undoException(cadenceId, occurrenceDate),
        );
        return { ok: true };
      } catch (err) {
        if (err instanceof ApiError && err.status === 409) return { ok: false, conflict: true };
        return { ok: false };
      }
    },
    [refreshCurrentWindow, showSnackbar, undoException],
  );

  const moveOccurrence = useCallback(
    async (occ: ScheduleOccurrence, newDateIso: string): Promise<CalendarMutationResult> => {
      if (!occ.cadence_id) return { ok: false };
      const cadenceId = occ.cadence_id;
      const occurrenceDate = occ.date;
      const idempotencyKey = crypto.randomUUID();
      try {
        await createScheduleException({
          idempotency_key: idempotencyKey,
          cadence_id: cadenceId,
          occurrence_date: occurrenceDate,
          action: "moved",
          new_date: newDateIso,
        });
        await refreshCurrentWindow();
        showSnackbar(
          movedSnackbarMessage(occ.title, moveTargetLabel(newDateIso, thisWeekDays)),
          () => void undoException(cadenceId, occurrenceDate),
        );
        return { ok: true };
      } catch (err) {
        if (err instanceof ApiError && err.status === 409) return { ok: false, conflict: true };
        return { ok: false };
      }
    },
    [refreshCurrentWindow, showSnackbar, thisWeekDays, undoException],
  );

  const moveOneOff = useCallback(
    async (occ: ScheduleOccurrence, newDateIso: string): Promise<CalendarMutationResult> => {
      if (occ.kind !== "assignment" || !occ.id) return { ok: false };
      const idempotencyKey = crypto.randomUUID();
      try {
        await patchAssignment(occ.id, { idempotency_key: idempotencyKey, due_date: newDateIso });
        await refreshCurrentWindow();
        showSnackbar(movedSnackbarMessage(occ.title, moveTargetLabel(newDateIso, thisWeekDays)));
        return { ok: true };
      } catch {
        return { ok: false };
      }
    },
    [refreshCurrentWindow, showSnackbar, thisWeekDays],
  );

  const createOneOff = useCallback(
    async (input: {
      title: string;
      assigneeId?: string;
      dueDateIso: string;
    }): Promise<CalendarMutationResult> => {
      const idempotencyKey = crypto.randomUUID();
      try {
        await createAssignment({
          idempotency_key: idempotencyKey,
          title: input.title,
          assignee_id: input.assigneeId,
          due_date: input.dueDateIso,
        });
        await refreshCurrentWindow();
        showSnackbar(addedSnackbarMessage(input.title, monthDayLabel(input.dueDateIso)));
        return { ok: true };
      } catch {
        return { ok: false };
      }
    },
    [refreshCurrentWindow, showSnackbar],
  );

  const today = todayLocal();
  const isOnToday =
    viewMode === "month"
      ? anchor.getFullYear() === today.getFullYear() && anchor.getMonth() === today.getMonth()
      : isSameDay(startOfWeek(anchor), startOfWeek(today));

  const goToday = useCallback(() => setAnchor(todayLocal()), []);
  const goPrev = useCallback(() => {
    setAnchor((prev) => (viewMode === "month" ? addMonths(prev, -1) : addDays(prev, -7)));
  }, [viewMode]);
  const goNext = useCallback(() => {
    setAnchor((prev) => (viewMode === "month" ? addMonths(prev, 1) : addDays(prev, 7)));
  }, [viewMode]);

  return {
    viewMode,
    setViewMode,
    anchor,
    setAnchor,
    scope: effectiveScope,
    setScope,
    canToggleScope: canHandOutWork,
    occurrences,
    flexible,
    // `loading`: true ONLY until the first successful load (or forever,
    // if that very first load itself fails and never yet succeeded) —
    // the one case that may legitimately show a full-panel placeholder.
    loading: !hasLoadedOnce,
    // `refreshing`: a background refetch is in flight (initial load
    // included) — a caller renders this as a small, non-blocking signal
    // ALONGSIDE its already-mounted content, never in place of it.
    refreshing,
    error,
    snackbar,
    dismissSnackbar,
    assignmentDateById,
    movedFromByAssignmentId,
    thisWeekDays,
    isOnToday,
    goToday,
    goPrev,
    goNext,
    createOneOff,
    moveOneOff,
    moveOccurrence,
    skipOccurrence,
    undoException,
    refresh: refreshCurrentWindow,
  };
}
